import React, { useState } from "react";

import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";

import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../../utils/formatEpisodeLengthToHumanFriendlyDuration";
import { createEspisodeService, YouTubeVideoMetadata } from "../../services";
import ButtonView from "../../../common/Button";

interface YouTubeImportProps {
  youtubeMetadata: YouTubeVideoMetadata | null;
  onYouTubeMetadataChange: (metadata: YouTubeVideoMetadata | null) => void;
}

export default function YouTubeImport({
  youtubeMetadata,
  onYouTubeMetadataChange,
}: YouTubeImportProps) {
  const [url, setUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const MIN_DURATION_SECONDS = 120;

  const handleLoad = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError("");
    onYouTubeMetadataChange(null);
    try {
      const metadata = await createEspisodeService.getYouTubeMetadata(
        url.trim(),
      );
      if (metadata.duration_in_seconds < MIN_DURATION_SECONDS) {
        setError(
          `Video duration must be at least 2 minutes. Current duration: ${formatEpisodeLengthToHumanFriendlyDuration(metadata.duration_in_seconds)}`,
        );
        return;
      }
      onYouTubeMetadataChange({ ...metadata, youtube_url: url.trim() } as any);
    } catch (err: any) {
      const message =
        err?.message ||
        err?.response?.data?.message ||
        "Could not load video. Check the URL and try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUrl("");
    setError("");
    onYouTubeMetadataChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLoad();
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!youtubeMetadata ? (
        <Paper
          elevation={0}
          sx={{
            border: "2px dashed",
            borderColor: "#2563eb",
            borderRadius: "12px",
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            backgroundColor: "#eff6ff",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              backgroundColor: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <YouTubeIcon sx={{ fontSize: 36, color: "#fff" }} />
          </Box>
          <Typography
            sx={{
              mb: 0.75,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "15px",
            }}
          >
            Import from YouTube
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3, fontSize: "13px" }}>
            Paste a YouTube video URL and we'll download it for you
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              maxWidth: 520,
              mx: "auto",
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError("");
              }}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: "#fff",
                },
              }}
            />
            <ButtonView
              variant="contained"
              label={loading ? "" : "Load"}
              type="button"
              handleOnClick={handleLoad}
              disabled={loading || !url.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                backgroundColor: "#dc2626",
                borderRadius: "8px",
                px: 2.5,
                fontSize: "13px",
                fontWeight: 600,
                height: 40,
                textTransform: "none",
                flexShrink: 0,
                minWidth: 90,
                "&:hover": { backgroundColor: "#b91c1c" },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "#fff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              mt: 2,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 14, color: "#2563eb" }} />
            <Typography
              sx={{ fontSize: "12px", fontWeight: 500, color: "#2563eb" }}
            >
              Minimum duration: 2 minutes
            </Typography>
          </Box>

          {error ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                p: 1.5,
                backgroundColor: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                textAlign: "left",
                maxWidth: 520,
                mx: "auto",
              }}
            >
              <ErrorOutlineIcon
                sx={{ color: "#dc2626", fontSize: 16, flexShrink: 0 }}
              />
              <Typography
                sx={{ color: "#dc2626", fontWeight: 500, fontSize: "12px" }}
              >
                {error}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              textAlign: "left",
              color: "#23272E",
              fontWeight: 700,
              fontSize: "1.125rem",
            }}
          >
            YouTube video loaded
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "text.secondary", lineHeight: 1.5 }}
          >
            Review the video details, then continue to episode details. You can
            change the URL at any time.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: (theme) =>
                theme.palette.mode === "light"
                  ? theme.palette.grey[50]
                  : theme.palette.grey[900],
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              {youtubeMetadata.thumbnail_url ? (
                <Box
                  component="img"
                  src={youtubeMetadata.thumbnail_url}
                  alt="YouTube thumbnail"
                  sx={{
                    width: 96,
                    height: 54,
                    borderRadius: "8px",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 96,
                    height: 54,
                    borderRadius: "8px",
                    flexShrink: 0,
                    backgroundColor: "#fef2f2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <YouTubeIcon sx={{ color: "#dc2626", fontSize: 28 }} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "#23272E",
                    wordBreak: "break-word",
                  }}
                >
                  {youtubeMetadata.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  YouTube
                  {" · "}
                  {formatEpisodeLengthToHumanFriendlyDuration(
                    youtubeMetadata.duration_in_seconds,
                  )}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    icon={
                      <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                    }
                    label="Ready to continue"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(22, 101, 52, 0.08)",
                      color: "#15803d",
                      fontWeight: 600,
                      borderRadius: "8px",
                      "& .MuiChip-icon": { color: "#15803d" },
                    }}
                  />
                </Box>
              </Box>
              <ButtonView
                variant="outlined"
                label="Change"
                type="button"
                handleOnClick={handleClear}
                sx={{
                  borderRadius: "8px",
                  px: 2,
                  fontSize: "12px",
                  fontWeight: 600,
                  height: 32,
                  textTransform: "none",
                  flexShrink: 0,
                  mt: -0.5,
                  borderColor: "divider",
                  color: "text.secondary",
                  "&:hover": { borderColor: "#dc2626", color: "#dc2626" },
                }}
              />
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
